import { Box, Collapse, IconButton, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useState } from "react";
import { PaginatedData } from "../../../../hooks/PaginationHooks";
import { TableHeaderWrapper, BasicTableRowStyles } from "../../../../components/common/Common.styles";
import BorderRow from "../../../../components/common/table/BorderRow";
import CellBox from "../../../../components/common/table/CellBox";
import { ServerSidePagination } from "../../../../components/common/table/ServerSidePagination";
import ServerSideTableColumnHeader from "../../../../components/common/table/ServerSideTableColumnHeader";
import StatusDisplay from "../../../../components/common/table/StatusDisplay";
import { useEditMode } from "../../../../store/selectors/tableManagementSelector";
import IncentivationInputField from "./IncentivationInputField";
import EmployeeInstancesIncentivationTable from "./EmployeeInstancesIncentivationTable";
import InstanceStatusIcon from "./InstanceStatusIcon";
import { ActionModuleEmployeeResponse, ActionModule } from "../../../../types/actionModule/actionModule";
import { BaseRewardCalculationInformation } from "../../../../types/actionModule/rewardCalculationInformation";
import { Dayjs } from "dayjs";

interface Props {
    paginatedData: PaginatedData<ActionModuleEmployeeResponse>
    actionModule: ActionModule;

    startDate: Dayjs;
    endDate: Dayjs;

    /**
     * This is called when the incentivation is changed regardless if it was the complete employee row
     * or an individual instance. Use this to keep track of the changes which need to be sent to the
     * backend.
     */
    onIncentivationUpdated: (
        employeeId: string,
        instanceId: number,
        rewardCalculationInformation: BaseRewardCalculationInformation
    ) => void;

    /**
     * This is called when one employee row was changed. Use this to update UI state
     */
    onEmployeeIncentivationChanged: (
        originalRow: ActionModuleEmployeeResponse,
        updatedRow: ActionModuleEmployeeResponse
    ) => void;
}

export default function IncentivationTable(props: Props) {
    const tableTemplate = "1fr 1.5fr 1.5fr 2fr 2fr 2fr 2fr 2fr 2fr";

    return <Box>
        <IncentivationTableHeader
            paginatedData={props.paginatedData}
            tableTemplate={tableTemplate} />
        <Box sx={{ display: "contents" }}>
            {
                props.paginatedData.items.map((u) => <IncentivationTableRow
                    key={u.id}
                    row={u}
                    startDate={props.startDate}
                    endDate={props.endDate}
                    actionModule={props.actionModule}
                    onIncentivationUpdated={(instanceId, update) => props.onIncentivationUpdated(u.id, instanceId, update)}
                    onRowChanged={(updated) => props.onEmployeeIncentivationChanged(u, updated)}
                    tableTemplate={tableTemplate} />)
            }
        </Box>
        <ServerSidePagination paginatedData={props.paginatedData} />
    </Box>
}

interface IncentivationTableHeaderProps {
    paginatedData: PaginatedData<any>;
    tableTemplate: string;
}

function IncentivationTableHeader(props: IncentivationTableHeaderProps) {
    const { t } = useTranslation();

    return <Box gridTemplateColumns={props.tableTemplate} display="grid">
        <TableHeaderWrapper>
            <ServerSideTableColumnHeader
                label=""
                firstCol={true}
            />
            <ServerSideTableColumnHeader
                fieldName="FirstName"
                isSorted={true}
                label={t("tableColumnHeaders.firstName")}
                paginatedData={props.paginatedData}
            />
            <ServerSideTableColumnHeader
                fieldName="LastName"
                isSorted={true}
                label={t("tableColumnHeaders.lastName")}
                paginatedData={props.paginatedData}
            />
            <ServerSideTableColumnHeader
                fieldName="Position"
                isSorted={true}
                label={t("tableColumnHeaders.position")}
                paginatedData={props.paginatedData}
            />
            <ServerSideTableColumnHeader
                fieldName="EmployeeNumber"
                isSorted={true}
                label={t("tableColumnHeaders.employeeNo")}
                paginatedData={props.paginatedData}
            />
            <ServerSideTableColumnHeader
                fieldName="LastActionDate" 
                isSorted={true}
                label={t("tableColumnHeaders.dateOfAction")}
                paginatedData={props.paginatedData}
            />
            <ServerSideTableColumnHeader
                isSorted={false}
                label={t("incentivationPage.incentivation")}
            />
            <ServerSideTableColumnHeader
                fieldName="points"
                label={t("tableColumnHeaders.points")}
            />
            <ServerSideTableColumnHeader
                fieldName="status"
                label={t("tableColumnHeaders.status")}
                lastCol={true}
            />
            <BorderRow />
        </TableHeaderWrapper>
    </Box>
}

interface IncentivationOverviewTableRowProps {
    row: ActionModuleEmployeeResponse;
    actionModule: ActionModule;
    onIncentivationUpdated: (
        instanceId: number,
        rewardCalculationInformation: BaseRewardCalculationInformation
    ) => void;
    onRowChanged: (updatedRow: ActionModuleEmployeeResponse) => void;
    tableTemplate: string;
    startDate: Dayjs;
    endDate: Dayjs;
}

function IncentivationTableRow(props: IncentivationOverviewTableRowProps) {
    const [open, setOpen] = useState(false);
    const editMode = useEditMode();

    return (
        <>
            {open && <BorderRow />}
            <Box gridTemplateColumns={props.tableTemplate} display="grid">
                <BasicTableRowStyles>
                    <CellBox firstCol={true}>
                        <IconButton
                            aria-label="expand row"
                            size="small"
                            onClick={() => setOpen(!open)}>
                            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                    </CellBox>
                    <CellBox>
                        <Typography>{props.row.firstName}</Typography>
                    </CellBox>
                    <CellBox>
                        <Typography>{props.row.lastName}</Typography>
                    </CellBox>
                    <CellBox>
                        <Typography>{props.row.position}</Typography>
                    </CellBox>
                    <CellBox>
                        <Typography>{props.row.employeeNumber}</Typography>
                    </CellBox>
                    <CellBox>
                        <Stack direction="row" alignItems="center" gap={1}>
                            {
                                props.row.lastInstanceStatus && <InstanceStatusIcon status={props.row.lastInstanceStatus} />
                            }
                            <Typography>{
                                props.row.lastInstanceCompletedAt
                                    ? new Date(props.row.lastInstanceCompletedAt).toLocaleDateString()
                                    : ""
                            }</Typography>
                        </Stack>
                    </CellBox>
                    <CellBox>
                        {
                            props.row.lastInstanceId !== null && !open ?
                                <IncentivationInputField
                                    actionModule={props.actionModule}
                                    isEditMode={editMode}
                                    employeeStatus={props.row.status}
                                    incentivationData={props.row.incentivationData}
                                    onChange={(updated) => {
                                        props.onIncentivationUpdated(
                                            props.row.lastInstanceId!,
                                            updated
                                        );
                                        props.onRowChanged({
                                            ...props.row,
                                            incentivationData: {
                                                ...props.row.incentivationData!,
                                                ...updated
                                            }
                                        });
                                    }} />
                                : <></>
                        }
                    </CellBox>
                    <CellBox>
                        <Typography>{props.row.totalPoints}</Typography>
                    </CellBox>
                    <CellBox lastCol={true} contentPosition="center">
                        <StatusDisplay status={props.row.status} />
                    </CellBox>
                </BasicTableRowStyles>
            </Box>
            <Collapse in={open} timeout="auto" unmountOnExit>
                <EmployeeInstancesIncentivationTable
                    actionModule={props.actionModule}
                    employeeRow={props.row}
                    startDate={props.startDate}
                    endDate={props.endDate}
                    onIncentivationUpdated={props.onIncentivationUpdated} />
            </Collapse>
        </>
    );
}